/**
 * Move a post's whole schedule to a different day, keeping every time of day intact.
 *
 * A post can carry a base time plus a per-platform time for each network — the Sep 30 set was
 * LinkedIn 15:00, Instagram 16:00, Facebook 17:00, X 18:00. Dragging it to another day must
 * move all of those together and change nothing but the date.
 *
 * Shifts by a whole number of **calendar days** rather than adding 24h × n milliseconds. Across
 * a daylight-saving boundary those differ: adding 14×24h to 16:00 lands on 15:00 or 17:00,
 * which is exactly the "retaining the time" this is supposed to preserve. Rebuilding the date
 * from its parts keeps the clock reading the same.
 */
export interface ShiftResult {
  /** New base time, ISO. */
  scheduledFor: string;
  /** New per-platform times, ISO, keyed by platform. Empty when the post had none. */
  variants: Record<string, string>;
}

/** Same clock time, `days` later (or earlier). Survives DST because it rebuilds the date. */
export const shiftByDays = (iso: string, days: number): string => {
  const d = new Date(iso);
  const moved = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + days,
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds(),
  );
  return moved.toISOString();
};

/** Whole days between two dates, ignoring the time of day on either. */
export const daysBetween = (from: Date, to: Date): number => {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  // Round rather than floor: a DST boundary makes the difference 23 or 25 hours.
  return Math.round((b.getTime() - a.getTime()) / 86400000);
};

/**
 * Work out the new schedule for a post dropped on `targetDay`.
 *
 * The offset comes from the post's **base** time, so every platform moves by the same number
 * of days and their relative order is preserved. Deriving it per platform would collapse a
 * staggered post onto one day.
 */
export const shiftScheduleToDay = (
  post: { scheduled_for?: string | null; schedule_variants?: Record<string, string> | null },
  targetDay: Date,
): ShiftResult | null => {
  if (!post.scheduled_for) return null;

  const offset = daysBetween(new Date(post.scheduled_for), targetDay);
  if (offset === 0) return null;   // dropped on the day it is already on

  const variants: Record<string, string> = {};
  for (const [platform, iso] of Object.entries(post.schedule_variants || {})) {
    if (iso) variants[platform] = shiftByDays(iso, offset);
  }

  return { scheduledFor: shiftByDays(post.scheduled_for, offset), variants };
};
